import apiClient from '../lib/axios';
import type { AccountSummary } from './twoFactorService';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'USER';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  lastActivityAt?: string;
  failedLoginAttempts: number;
  lockoutUntil?: string;
  twoFactorEnabled: boolean;
}

export interface InviteUserDto {
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'USER';
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  role?: 'ADMIN' | 'USER';
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateOwnProfileDto {
  firstName: string;
  lastName: string;
}

class UserService {
  /**
   * Get all users with optional filters
   */
  async getUsers(params?: {
    search?: string;
    role?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }) {
    const response = await apiClient.get('/users', { params });
    return response.data;
  }

  /**
   * Get a single user by ID
   */
  async getUser(id: string): Promise<User> {
    const response = await apiClient.get(`/users/${id}`);
    return response.data.data;
  }

  /**
   * Invite a new user (sends email invitation)
   */
  async inviteUser(data: InviteUserDto) {
    const response = await apiClient.post('/users', data);
    return response.data;
  }

  /**
   * Update an existing user
   */
  async updateUser(id: string, data: UpdateUserDto): Promise<User> {
    const response = await apiClient.put(`/users/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete a user
   */
  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  }

  /**
   * Activate a user
   */
  async activateUser(id: string) {
    const response = await apiClient.post(`/users/${id}/activate`);
    return response.data.data;
  }

  /**
   * Deactivate a user
   */
  async deactivateUser(id: string) {
    const response = await apiClient.post(`/users/${id}/deactivate`);
    return response.data.data;
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordDto) {
    const response = await apiClient.post('/users/change-password', data);
    return response.data;
  }

  /**
   * Update your own first and last name.
   *
   * Returns the narrow AccountSummary, not the full User. The caller caches
   * this in localStorage, so the endpoint deliberately sends only what the app
   * displays and nothing else from the row.
   */
  async updateOwnProfile(data: UpdateOwnProfileDto): Promise<AccountSummary> {
    const response = await apiClient.patch('/users/me', data);
    return response.data.data;
  }

  /**
   * Resend invitation email
   */
  async resendInvitation(id: string) {
    const response = await apiClient.post(`/users/${id}/resend-invitation`);
    return response.data;
  }

  /**
   * Cancel a pending invitation (hard-deletes a user who never onboarded)
   */
  async cancelInvitation(id: string) {
    const response = await apiClient.delete(`/users/${id}/invitation`);
    return response.data;
  }

  /**
   * Unlock a locked user account
   */
  async unlockUser(id: string) {
    const response = await apiClient.post(`/users/${id}/unlock`);
    return response.data;
  }

  /**
   * Trigger password reset for a user (admin action)
   */
  async resetUserPassword(id: string) {
    const response = await apiClient.post(`/users/${id}/reset-password`);
    return response.data;
  }
}

export const userService = new UserService();
